import type { OrderMessageWithUser } from 'src/api/db/apiOrderMessage'
import type { Quote_ } from 'src/api/db/apiQuote'
import type { QuoteItem_ } from 'src/api/db/apiQuoteItem'
import type { ShippingLocation_ } from 'src/api/db/apiShippingLocations'
import type { ShipmentWithLocation } from 'src/api/db/apiShipment'
import { IMAGE_URLS } from 'src/assets/urlLinks'
import type { ChatMessage } from 'src/shared/components/SaraChat/types/chatMessage'
import {
  APPROVED_PROOFS_STATUS,
  APPROVED_QUOTE_STATUS,
  CONTACTED_SALES_STATUS,
  DELIVERED_STATUS,
  SALES_APPROVED_QUOTE_STATUS,
  SHIPPING_QUOTE_STATUS,
  STARTED_PRODUCTION_STATUS,
} from 'src/shared/consts/quoteStatus'
import type { RequestContextValue } from 'src/pages/_myAccount/Requests/Request/hooks/RequestContext'
import type { InvoiceData } from 'src/pages/_myAccount/Requests/Request/components/BillingDocument/types'
import type { RequestTabId } from 'src/pages/_myAccount/Requests/Request/types'
import type { ProofStatus } from 'src/shared/utils/proofUrls'
import { buildDemoCanvasData } from './demoCanvasData'

export const DEMO_PUBLIC_ID = 'home-demo'
export const DEMO_QUOTE_ID = 0

const HOODIE_IMG = IMAGE_URLS.images.hoodie
const TEE_IMG = IMAGE_URLS.images.tshirt
const MOCKUP_HOODIE = '/demo/hoodie-mockup.png'
const MOCKUP_HOODIE_DETAIL = '/demo/hoodie-mockup-detail.png'
const MOCKUP_TEE = '/demo/tee-mockup.png'
const MOCKUP_TOTE = '/demo/tote-mockup.png'
const TOTE_IMG = MOCKUP_TOTE

const DEMO_T0 = Date.parse('2026-08-19T09:12:00.000Z')

type Locale = 'en' | 'sv'

const copy = (locale: Locale, en: string, sv: string) =>
  locale === 'sv' ? sv : en

const at = (seconds: number) =>
  new Date(DEMO_T0 + seconds * 1000).toISOString()

const color = (id: number, name: string, imageUrl: string) => ({
  id,
  color: name,
  created_at: '2026-08-19T08:00:00.000Z',
  item_code: null,
  product_variant_id: id,
  sizes: null,
  product_image: [
    {
      id,
      created_at: '2026-08-19T08:00:00.000Z',
      image_url: imageUrl,
      order: 0,
      product_variant_color_id: id,
    },
  ],
})

const demoItem = ({
  id,
  subId,
  name,
  imageUrl,
  mockupUrls,
  unitPrice,
  colorId,
  colorName,
  decoration,
}: {
  id: number
  subId: number
  name: string
  imageUrl: string
  mockupUrls?: string[] | null
  unitPrice: number
  colorId: number
  colorName: string
  decoration: string
}): QuoteItem_ => {
  const variantColor = color(colorId, colorName, imageUrl)
  return {
    id,
    quote_id: DEMO_QUOTE_ID,
    product_id: id,
    product_variant_color_id: colorId,
    quantity: 50,
    sizes: null,
    type: 'product',
    notes: null,
    created_at: at(0),
    unit_price: unitPrice,
    discount: 0,
    description: name,
    product: {
      id,
      item_name: name,
      item_description: name,
      source: null,
      moq: 25,
      price: unitPrice,
      print_codes: null,
      article_number: `KM-${id}`,
      klayder_recommends: true,
      klayder_recommendation_text: '',
      related_product_ids: null,
      category_product: [],
      product_variant: [
        {
          product_variant_color: [variantColor],
        },
      ],
    },
    quote_sub_item: [
      {
        id: subId,
        created_at: at(0),
        d_colors: 1,
        d_format: null,
        d_number_of_stitches: null,
        d_size: '10x8 cm',
        d_size_range: null,
        description: decoration,
        discount: 0,
        mockup_urls: mockupUrls ?? null,
        notes: null,
        print_code: 'DTG',
        quantity: 50,
        quote_item_id: id,
        type: 'decoration',
        unit_price: 28,
        vendor: null,
        design: [],
      },
    ],
  } as QuoteItem_
}

export const allDemoItems = (withMockups: boolean): QuoteItem_[] => [
  demoItem({
    id: 101,
    subId: 1001,
    name: 'Creator hoodie',
    imageUrl: HOODIE_IMG,
    mockupUrls: withMockups ? [MOCKUP_HOODIE, MOCKUP_HOODIE_DETAIL] : null,
    unitPrice: 289,
    colorId: 201,
    colorName: 'Black',
    decoration: 'Front print || Tryck fram',
  }),
  demoItem({
    id: 102,
    subId: 1002,
    name: 'Creator t-shirt',
    imageUrl: TEE_IMG,
    mockupUrls: withMockups ? [MOCKUP_TEE] : null,
    unitPrice: 149,
    colorId: 202,
    colorName: 'White',
    decoration: 'Front print || Tryck fram',
  }),
  demoItem({
    id: 103,
    subId: 1003,
    name: 'Organic tote',
    imageUrl: TOTE_IMG,
    mockupUrls: withMockups ? [MOCKUP_TOTE] : null,
    unitPrice: 79,
    colorId: 203,
    colorName: 'Natural',
    decoration: 'Front print || Tryck fram',
  }),
]

const demoProofLinks = (status: ProofStatus) => [
  {
    proof_id: 'demo-proof-hoodie',
    status,
    sort_order: 0,
    linked_at: '2026-08-19T12:00:00.000Z',
    proof: {
      id: 'demo-proof-hoodie',
      url: MOCKUP_HOODIE,
      created_at: '2026-08-19T12:00:00.000Z',
    },
  },
  {
    proof_id: 'demo-proof-tee',
    status,
    sort_order: 1,
    linked_at: '2026-08-19T12:02:00.000Z',
    proof: {
      id: 'demo-proof-tee',
      url: MOCKUP_TEE,
      created_at: '2026-08-19T12:02:00.000Z',
    },
  },
  {
    proof_id: 'demo-proof-tote',
    status,
    sort_order: 2,
    linked_at: '2026-08-19T12:04:00.000Z',
    proof: {
      id: 'demo-proof-tote',
      url: TOTE_IMG,
      created_at: '2026-08-19T12:04:00.000Z',
    },
  },
]

export const DEMO_SHIPPING_LOCATION: ShippingLocation_ = {
  id: 9001,
  active: true,
  default: true,
  name: 'HQ',
  company_name: 'Northwind AB',
  first_name: 'Alex',
  last_name: 'Bergman',
  emails: null,
  phone_number: '+46 70 123 45 67',
  street_address: 'Kungsgatan',
  street_number: '12',
  street_address_2: null,
  zip_code: '111 43',
  city: 'Stockholm',
  country: 'Sweden',
  org_id: null,
  created_at: '2026-08-19T08:00:00.000Z',
}

const issuedInvoice = (): InvoiceData => ({
  invoiceNumber: 'KL-1042',
  issueDate: '2026-08-12',
  supplyDate: '2026-08-12',
  dueDate: '2026-09-11',
  paymentTermsDays: 30,
  paymentReference: '1042',
  bankgiro: '000-0000',
  plusgiro: '',
  iban: '',
  bic: '',
  currency: 'SEK',
  vatRate: 25,
  fTaxApproved: true,
  reverseCharge: false,
  ourReference: 'Linnea Holm',
  yourReference: 'Alex Bergman',
  notes: '',
  seller: {
    name: 'Klayder AB',
    addressLine1: 'Svandammsvägen 36',
    addressLine2: '',
    postal: '126 34',
    city: 'Hägersten',
    country: 'Sweden',
    orgNumber: '559433-8104',
    vatNumber: 'SE559433810401',
    email: 'carmelo@klayder.com',
  },
  buyer: {
    name: 'Northwind AB',
    addressLine1: 'Kungsgatan 12',
    addressLine2: '',
    postal: '111 43',
    city: 'Stockholm',
    country: 'Sweden',
    orgNumber: '556677-8899',
    vatNumber: 'SE556677889901',
    email: 'finance@northwind.se',
  },
  status: 'issued',
  createdAt: '2026-08-12T10:00:00.000Z',
  updatedAt: '2026-08-12T10:00:00.000Z',
})

const buildDemoQuote = ({
  status,
  withProofs = false,
  proofStatus = 'approved',
  withInvoice = false,
  locale,
}: {
  status: string
  withProofs?: boolean
  proofStatus?: ProofStatus
  withInvoice?: boolean
  locale: Locale
}): Quote_ => ({
  id: DEMO_QUOTE_ID,
  created_at: '2026-08-19T08:00:00.000Z',
  canvas_data: buildDemoCanvasData(locale),
  comment: null,
  delivery_estimate: null,
  invoice_data: withInvoice ? issuedInvoice() : null,
  name: copy(locale, 'Q3 onboarding kit', 'Onboardingkit Q3'),
  open_id: null,
  org_id: null,
  payment_option: 'invoice',
  payment_reference: null,
  payment_terms: 30,
  proof_urls: null,
  public_id: DEMO_PUBLIC_ID,
  sort_mode: null,
  source: null,
  status,
  organization: {
    name: 'Northwind',
    currency: 'SEK',
    org_number: '556677-8899',
    vat_number: 'SE556677889901',
    billing_email: 'finance@northwind.se',
    billing_address_line1: 'Kungsgatan 12',
    billing_postcode: '111 43',
    billing_city: 'Stockholm',
    billing_country: 'Sweden',
    default_invoice_reference: 'Alex Bergman',
  },
  quote_proof: withProofs ? demoProofLinks(proofStatus) : [],
})

const teamMsg = (
  id: number,
  createdAt: string,
  content: string,
  role: string,
  user?: OrderMessageWithUser['user']
): OrderMessageWithUser => ({
  id,
  attachment_urls: [],
  content,
  conversation_id: 0,
  created_at: createdAt,
  quote_id: DEMO_QUOTE_ID,
  role,
  user_id: null,
  user: user ?? null,
})

export type DemoSnapshot = {
  quote: Quote_
  quoteItems: QuoteItem_[]
  saraMessages: Array<{ message: ChatMessage; isPrivate: boolean }>
  teamMessages: OrderMessageWithUser[]
  isSaraLoading: boolean
  showPingCta: boolean
  demoShipment?: ShipmentWithLocation
}

const DEMO_CUSTOMER = {
  first_name: 'Alex',
  last_name: 'Bergman',
  email: 'alex@northwind.se',
}

const DEMO_SALES = {
  first_name: 'Linnea',
  last_name: 'Holm',
  email: 'linnea@klayder.com',
}

const teamConversation = (locale: Locale) => {
  const brief = teamMsg(
    1,
    at(20),
    copy(
      locale,
      'Hi Linnea! We need a kit for 50 new hires. Do you have any suggestions for us? :)',
      'Hej Linnea! Vi behöver ett kit till 50 nyanställda. Har du några förslag till oss? :)'
    ),
    'customer',
    DEMO_CUSTOMER
  )
  const suggesting = teamMsg(
    2,
    at(95),
    copy(
      locale,
      "Yes! I'd go hoodie, tee and tote. Adding them now.",
      'Ja! Jag hade kört hoodie, t-shirt och tygkasse. Lägger in dem nu.'
    ),
    'admin',
    DEMO_SALES
  )
  const mockupsReady = teamMsg(
    3,
    at(210),
    copy(
      locale,
      'Mockups are ready. Have a look, quote is in Checkout.',
      'Mockupsen är klara. Kika, offerten ligger i Kassan.'
    ),
    'admin',
    DEMO_SALES
  )
  const quoteApproved = teamMsg(
    4,
    at(280),
    copy(locale, 'Looks great, quote approved.', 'Ser bra ut, offerten är godkänd.'),
    'customer',
    DEMO_CUSTOMER
  )
  const proofsReady = teamMsg(
    5,
    at(310),
    copy(
      locale,
      'Nice. Proofs are in Checkout.',
      'Toppen. Korrekturerna ligger i Kassan.'
    ),
    'admin',
    DEMO_SALES
  )
  const proofsApproved = teamMsg(
    6,
    at(370),
    copy(
      locale,
      'Proofs look good, approved! :)',
      'Korrekturerna ser bra ut, godkända! :)'
    ),
    'customer',
    DEMO_CUSTOMER
  )

  return {
    brief,
    suggesting,
    mockupsReady,
    quoteApproved,
    proofsReady,
    proofsApproved,
  }
}

const teamMessagesForStatus = (locale: Locale) => {
  const conversation = teamConversation(locale)
  return [
    conversation.brief,
    conversation.suggesting,
    conversation.mockupsReady,
    conversation.quoteApproved,
    conversation.proofsReady,
    conversation.proofsApproved,
  ]
}

const localDate = (daysFromToday: number) => {
  const date = new Date()
  date.setHours(12, 0, 0, 0)
  date.setDate(date.getDate() + daysFromToday)
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const shipment = (locale: Locale): ShipmentWithLocation => ({
  id: 1,
  created_at: `${localDate(-6)}T08:00:00.000Z`,
  notes: copy(
    locale,
    'On the way in one carton to HQ reception.',
    'På väg i en kartong till receptionen på HQ.'
  ),
  quote_id: DEMO_QUOTE_ID,
  quote_shipping_location_id: null,
  shipment_id: 'DHL-448291',
  shipment_url: 'https://www.dhl.com/se-sv/home.html',
  shipping_date_end: localDate(4),
  shipping_date_start: localDate(-6),
  status: 'in_transit',
  quote_shipping_location: {
    shipping_locations: {
      name: 'HQ',
      company_name: 'Northwind AB',
    },
  },
})

const isOrderPlacedStatus = (status: string) =>
  status === STARTED_PRODUCTION_STATUS ||
  status === SHIPPING_QUOTE_STATUS ||
  status === DELIVERED_STATUS

const hasApprovedProofs = (status: string) =>
  status === APPROVED_PROOFS_STATUS || isOrderPlacedStatus(status)

const hasRequestedProofs = (status: string) =>
  status === APPROVED_QUOTE_STATUS || hasApprovedProofs(status)

export const buildDemoSnapshot = (
  tab: RequestTabId,
  locale: Locale,
  checkoutStatus: string = SALES_APPROVED_QUOTE_STATUS
): DemoSnapshot => {
  const status =
    tab === 'delivery'
      ? SHIPPING_QUOTE_STATUS
      : tab === 'checkout'
        ? checkoutStatus
        : CONTACTED_SALES_STATUS

  return {
    quoteItems: allDemoItems(true),
    saraMessages: [],
    teamMessages: teamMessagesForStatus(locale),
    isSaraLoading: false,
    showPingCta: false,
    quote: buildDemoQuote({
      status,
      withProofs: tab === 'delivery' || hasRequestedProofs(status),
      proofStatus: tab === 'delivery' || hasApprovedProofs(status)
        ? 'approved'
        : 'pending',
      withInvoice: tab === 'delivery' || isOrderPlacedStatus(status),
      locale,
    }),
    demoShipment:
      tab === 'delivery' || isOrderPlacedStatus(status)
        ? shipment(locale)
        : undefined,
  }
}

export const toRequestContextValue = (
  snapshot: DemoSnapshot,
  extras?: Pick<
    RequestContextValue,
    'onDemoAdvance' | 'demoShippingLocations'
  >
): RequestContextValue => ({
  publicId: DEMO_PUBLIC_ID,
  quote: snapshot.quote,
  quoteItems: snapshot.quoteItems,
  isError: false,
  isQuoteMissing: false,
  isQuotePending: false,
  isQuoteItemsPending: false,
  refetchQuote: () => undefined,
  isDemo: true,
  demoShipment: snapshot.demoShipment,
  demoShippingLocations: extras?.demoShippingLocations,
  onDemoAdvance: extras?.onDemoAdvance,
})
